/**
 * Symptom definitions and preferences. Architecture Section 7.
 *
 * Kept separate from EntryContext because the two change on completely
 * different rhythms: entries change many times a day, definitions change
 * almost never. Merging them would re-render the calendar every time a
 * preference moved.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { SymptomId } from '@domain/types'
import {
  DEFAULT_SETTINGS,
  hideSymptom,
  loadSettings,
  loadSymptoms,
  setPredictionsSuspended,
  saveSettings,
  type Settings,
  type SymptomDef,
  type PremiumState,
} from '@shared/data/private'
import {
  PAYWALL_ENABLED,
  activateLicenseKey,
  validateLicenseKey,
  deactivateLicenseKey,
} from '@shared/utils/polar'

export interface SettingsContextValue {
  settings: Settings
  symptoms: readonly SymptomDef[]
  /** Visible, ordered — what a picker should offer. */
  activeSymptoms: readonly SymptomDef[]
  setSymptomHidden: (id: SymptomId, hidden: boolean) => Promise<void>
  /** FR-412. The reason is optional and never required. */
  suspendPredictions: (
    suspended: boolean,
    reason?: Settings['predictions']['reason'],
  ) => Promise<void>
  isPremium: boolean
  premiumLoading: boolean
  premiumError: string | null
  activatePremium: (key: string) => Promise<boolean>
  deactivatePremium: () => Promise<boolean>
  verifyPremiumOnce: () => Promise<boolean>
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined)

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [symptoms, setSymptoms] = useState<readonly SymptomDef[]>([])
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS)
  const [premiumLoading, setPremiumLoading] = useState(false)
  const [premiumError, setPremiumError] = useState<string | null>(null)

  const isPremium = useMemo(
    () => (PAYWALL_ENABLED ? settings.premium?.status === 'active' : true),
    [settings],
  )

  /*
   * Every read and every write takes a ticket, and a result is applied only if no
   * later ticket has already been applied. Without this, the state was
   * last-*resolved*-wins rather than last-*requested*-wins.
   *
   * The failing case: the mount read is still awaiting IndexedDB when the user
   * turns estimates off. The write completes, state says suspended, then the
   * in-flight read resolves with the value it fetched BEFORE the write and puts
   * suspended back to false. The button flips back on its own.
   *
   * That is not cosmetic. FR-412 exists so somebody who does not want to see a
   * next-period estimate — after a loss, during treatment, while trying to
   * conceive — does not have to. Here the choice is stored correctly and then
   * silently disregarded by the UI, so the estimate reappears on the screen of
   * the one person who asked for it not to. Slow storage makes it more likely,
   * which means an old phone is where it fails.
   *
   * Found by a test that had been dismissed as a flake. It was reporting this.
   */
  const issued = useRef(0)
  const applied = useRef(0)

  /** Applies `commit` only if this ticket is still the newest one resolved. */
  const settle = useCallback((ticket: number, commit: () => void) => {
    if (ticket <= applied.current) return
    applied.current = ticket
    commit()
  }, [])

  const load = useCallback(async () => {
    const ticket = ++issued.current
    const [loadedSymptoms, loadedSettings] = await Promise.all([
      loadSymptoms(),
      loadSettings(),
    ])
    settle(ticket, () => {
      setSymptoms(loadedSymptoms)
      setSettings(loadedSettings)
    })
  }, [settle])

  useEffect(() => {
    // The lint rule traces `reload` to a setState and assumes a synchronous
    // cascade. It is not one: every setState runs after an awaited IndexedDB
    // read. Loading persisted state on mount is what an effect is for.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load().catch(() => {
      // A definitions read failure must not take the app down: the calendar and
      // flow logging work without a symptom list. EntryContext surfaces the
      // storage problem (NFR-05); this degrades to an empty picker.
    })
  }, [load])

  const verifyPremiumOnce = useCallback(async (): Promise<boolean> => {
    if (!PAYWALL_ENABLED) return true
    const current = settings.premium
    if (!current || !current.licenseKey) {
      return false
    }

    // If validated in the last 24 hours, skip online check
    const lastVal = new Date(current.lastValidated).getTime()
    const ageMs = Date.now() - lastVal
    if (ageMs < 24 * 60 * 60 * 1000) {
      return current.status === 'active'
    }

    if (!navigator.onLine) {
      // Offline: trust cache if it is under 7 days old
      const offlineValid = ageMs < 7 * 24 * 60 * 60 * 1000
      return offlineValid && current.status === 'active'
    }

    setPremiumLoading(true)
    try {
      const res = await validateLicenseKey(
        current.licenseKey,
        current.activationId !== 'no-activation-required' ? current.activationId : undefined,
      )
      if (res.success && res.licenseKey) {
        const updated: PremiumState = {
          ...current,
          lastValidated: new Date().toISOString(),
          status: 'active',
        }
        const nextSettings = { ...settings, premium: updated }
        const ticket = ++issued.current
        const saved = await saveSettings(nextSettings)
        settle(ticket, () => setSettings(saved))
        return true
      } else {
        const status = res.error?.includes('expired') ? 'expired' : 'revoked'
        const updated: PremiumState = {
          ...current,
          lastValidated: new Date().toISOString(),
          status: status as any,
        }
        const nextSettings = { ...settings, premium: updated }
        const ticket = ++issued.current
        const saved = await saveSettings(nextSettings)
        settle(ticket, () => setSettings(saved))
        return false
      }
    } catch {
      const offlineValid = ageMs < 7 * 24 * 60 * 60 * 1000
      return offlineValid && current.status === 'active'
    } finally {
      setPremiumLoading(false)
    }
  }, [settings, settle])

  const activatePremium = useCallback(async (key: string): Promise<boolean> => {
    if (!key.trim()) {
      setPremiumError('License key is required.')
      return false
    }
    setPremiumLoading(true)
    setPremiumError(null)
    try {
      const deviceLabel = `Device (${navigator.userAgent.slice(0, 30)})`
      const res = await activateLicenseKey(key, deviceLabel)
      if (res.success && res.activation && res.licenseKey) {
        const newState: PremiumState = {
          licenseKey: key,
          activationId: res.activation.id,
          lastValidated: new Date().toISOString(),
          status: 'active',
        }
        const nextSettings = { ...settings, premium: newState }
        const ticket = ++issued.current
        const saved = await saveSettings(nextSettings)
        settle(ticket, () => setSettings(saved))
        return true
      } else if (
        res.error &&
        (res.error.includes('does not support activations') || res.error.includes('/validate'))
      ) {
        console.log('[SettingsContext] License key does not support activations, falling back to validation.')
        const valRes = await validateLicenseKey(key)
        if (valRes.success && valRes.licenseKey) {
          const newState: PremiumState = {
            licenseKey: key,
            activationId: 'no-activation-required',
            lastValidated: new Date().toISOString(),
            status: 'active',
          }
          const nextSettings = { ...settings, premium: newState }
          const ticket = ++issued.current
          const saved = await saveSettings(nextSettings)
          settle(ticket, () => setSettings(saved))
          return true
        } else {
          setPremiumError(valRes.error || 'Validation failed.')
          return false
        }
      } else {
        setPremiumError(res.error || 'Activation failed.')
        return false
      }
    } catch (e: any) {
      setPremiumError(e.message || 'Failed to activate license.')
      return false
    } finally {
      setPremiumLoading(false)
    }
  }, [settings, settle])

  const deactivatePremium = useCallback(async (): Promise<boolean> => {
    const current = settings.premium
    if (!current) return true

    setPremiumLoading(true)
    setPremiumError(null)
    try {
      if (
        navigator.onLine &&
        current.activationId &&
        current.activationId !== 'no-activation-required'
      ) {
        await deactivateLicenseKey(current.licenseKey, current.activationId)
      }
      const nextSettings = { ...settings, premium: undefined }
      const ticket = ++issued.current
      const saved = await saveSettings(nextSettings)
      settle(ticket, () => setSettings(saved))
      return true
    } catch (e: any) {
      setPremiumError(e.message || 'Failed to deactivate license.')
      return false
    } finally {
      setPremiumLoading(false)
    }
  }, [settings, settle])

  // Trigger verification on boot when settings are first loaded
  const verifiedOnBoot = useRef(false)
  useEffect(() => {
    if (settings.premium && !verifiedOnBoot.current) {
      verifiedOnBoot.current = true
      void verifyPremiumOnce()
    }
  }, [settings.premium, verifyPremiumOnce])

  const setSymptomHidden = useCallback(
    async (id: SymptomId, hidden: boolean) => {
      await hideSymptom(id, hidden)
      await load()
    },
    [load],
  )

  const suspendPredictions = useCallback(
    async (suspended: boolean, reason?: Settings['predictions']['reason']) => {
      const ticket = ++issued.current
      const saved = await setPredictionsSuspended(suspended, reason)
      settle(ticket, () => setSettings(saved))
    },
    [settle],
  )

  const activeSymptoms = useMemo(
    () => symptoms.filter((s) => !s.hidden).sort((a, b) => a.order - b.order),
    [symptoms],
  )

  const value = useMemo<SettingsContextValue>(
    () => ({
      settings,
      symptoms,
      activeSymptoms,
      setSymptomHidden,
      suspendPredictions,
      isPremium,
      premiumLoading,
      premiumError,
      activatePremium,
      deactivatePremium,
      verifyPremiumOnce,
    }),
    [
      settings,
      symptoms,
      activeSymptoms,
      setSymptomHidden,
      suspendPredictions,
      isPremium,
      premiumLoading,
      premiumError,
      activatePremium,
      deactivatePremium,
      verifyPremiumOnce,
    ],
  )

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

export function useSettings(): SettingsContextValue {
  const value = useContext(SettingsContext)
  if (value === undefined) throw new Error('useSettings used outside SettingsProvider')
  return value
}
